import {Animation, Combined, Layer, Object, Transformable} from "../../../feature/project/Shot";
import {Button, Flex, Input, Modal, Space, Tabs, Typography} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {ShotState} from "./ShotViewport";
import {Status} from "../../../feature/project/status";
import {fetchTags} from "../../../feature/project/projectsSlice";
import {Tag} from "../../../feature/project/fields";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {useParams} from "react-router-dom";
import {TagFlow} from "../../../components/TagFlow";
import LayerList from "../../common/LayerList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlay, faPlus, faStop} from "@fortawesome/free-solid-svg-icons";
import {deleteLayer, saveLayer, saveObjectLayer} from "../../../feature/project/layerSlice";
import {Starfield} from "../../../components/Starfiled";
import {LayerEditModal} from "./LayerEditModal";
import {ElementProperties} from "./ElementProperties";
import {ElementsCollection} from "../../common/ElementsCollection";
import {ElementInfo} from "./ElementInfo";
import {
    addObjectTag,
    fetchObjectsCollection,
    fetchObjectsLore, removeObjectTag,
    saveObject
} from "../../../feature/project/objectsSlice";
import {AnimationForm, compareAnimations} from "./AnimationForm";
import {
    saveObjectAnimation,
    deleteAnimation
} from "../../../feature/project/animationsSlice";
import {AnimatableElement} from "./AnimatableElement";
import {EditElement} from "./EditElement";

const {confirm} = Modal;

export const ObjectEditModal = (
    props: {
        object: Object,
        onSave: (object: Object) => void,
        onCancel: () => void
    }
) => {
    const dispatch = useAppDispatch()
    const {projectId, shotId} = useParams()
    const [title, setTitle] = React.useState(props.object.title)
    const [tags, setTags] = React.useState<Array<Tag>>(props.object.tags || [])
    const [showSelectObject, setShowSelectObject] = React.useState(false)

    const [hasChanges, setHasChanges] = React.useState(false)

    useEffect(() => {
        setHasChanges(title !== props.object.title)
    }, [title])

    useEffect(() => {
        setTitle(props.object.title)
        setTags(props.object.tags || [])
    }, [props.object, props.object.tags])

    useEffect(() => {
        if (!props.object.id) {
            setShowSelectObject(true)
        }
    }, [props.object.id])

    return (
        <>
            <Modal
                open={true}
                title={props.object.id ? 'Edit Object' : 'Add Object'}
                width={1000}
                okText={'Save'}
                cancelText={'Close'}
                onCancel={props.onCancel}
                okButtonProps={{disabled: !hasChanges}}
                onOk={() => props.onSave({
                    ...props.object,
                    title: title,
                    tags: tags
                })}
            >
                {showSelectObject ?
                    <ObjectSelectView onObjectSelected={(object) => {
                        if (projectId && shotId) {
                            const obj = {
                                ...object,
                                id: undefined,
                                shot: {
                                    id: parseInt(shotId)
                                },
                                layers: object?.layers?.map(layer => {
                                    return {
                                        ...layer,
                                        id: undefined,
                                        animations: layer.animations?.map(anim => {
                                            return {...anim, id: undefined}
                                        })
                                    }
                                })
                            }
                            dispatch(saveObject({projectId: parseInt(projectId), object: obj as Object}))
                            setShowSelectObject(false)
                        }
                    }}/>
                    :
                    <ObjectEditView
                        object={props.object}
                        title={title}
                        tags={tags}
                        onTitleChanged={(value) => setTitle(value)}
                        onTagsChanged={(value) => setTags(value)}
                    />}
            </Modal>
        </>
    )
}

const ObjectSelectView = (props: { onObjectSelected: (object: Object) => void }) => {
    const dispatch = useAppDispatch()
    const {projectId} = useParams()
    const {storyId} = useParams()
    const objectsCollection = useAppSelector(state => state.objects.collection)
    const objectsLore = useAppSelector(state => state.objects.lore)
    const status = useAppSelector(state => state.objects.status)

    useEffect(() => {
        if (status === Status.Idle && storyId && projectId) {
            dispatch(fetchObjectsCollection({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
            dispatch(fetchObjectsLore({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
        }
    }, [status, storyId, projectId])

    return (
        <div style={{height: 400}}>
            <Tabs
                defaultActiveKey="1"
                items={[
                    {
                        label: `Collection`,
                        key: "1",
                        children: <ElementsCollection
                            animated={true}
                            key={"1"} elements={objectsCollection || []}
                            onSelect={(element) => {
                                props.onObjectSelected(element as Object)
                            }}/>,
                    },
                    {
                        label: `Lore`,
                        key: "2",
                        children: <ElementsCollection
                            animated={false}
                            key={"2"}
                            elements={objectsLore || []}
                            onSelect={(element: any) => {
                                props.onObjectSelected(
                                    {title: (element as Object).title, component: element?.component}
                                )
                            }}/>,
                    },
                ]}
            />
        </div>
    )
}

const ObjectEditView = (props: {
    object: Object,
    title?: string,
    tags: Array<Tag>,
    onTitleChanged: (value: string) => void,
    onTagsChanged: (value: Array<Tag>) => void
}) => {
    const status = useAppSelector(state => state.objects.status)
    const dispatch = useAppDispatch()
    const {projectId, storyId} = useParams()
    const [scale, setScale] = React.useState(1)
    const [height, setHeight] = React.useState(0)
    const [width, setWidth] = React.useState(0)
    const allTags = useAppSelector(state => state.projects.tags)
    const [layer, setLayer] = React.useState<Combined | undefined>(undefined)
    const [showEditLayerModal, setShowEditLayerModal] = React.useState<Layer | undefined>(undefined)
    const [play, setPlay] = React.useState(ShotState.Idle)
    const [played, setPlayed] = useState<Set<number>>(new Set())
    const [info, setInfo] = React.useState({x: 0, y: 0, z: 0, scale: 1})
    const [animation, setAnimation] = React.useState<Animation | undefined>(undefined)
    const [selectPivotPoint, setSelectPivotPoint] = React.useState(false)

    useEffect(() => {
        if (layer) {
            const selectedLayer: Layer | undefined = props.object.layers?.find(lay => lay.id === layer?.id)
            setLayer({...selectedLayer, type: 'Layer'})
        }
        if (showEditLayerModal) {
            const editedLayer: Layer | undefined = props.object.layers?.find(lay => lay.id === showEditLayerModal?.id)
            setShowEditLayerModal({...editedLayer, type: 'Layer'})
        }
    }, [props.object])

    useEffect(() => {
        setInfo({x: layer?.x || 0, y: layer?.y || 0, z: layer?.z || 0, scale: layer?.scale || 1})
    }, [layer])

    useEffect(() => {
        setScale(width > height ? width / 1920 : height / 1920)
    }, [width, height, props.object])

    useEffect(() => {
        if (played.size === ((props.object.layers?.length ?? 0) + 1) && play || 0 === ((props.object.layers?.length ?? 0) + 1) && play) {
            setPlayed(new Set())
            setPlay(ShotState.Idle)
        }
    }, [played, play]);

    useEffect(() => {
        if (status === Status.Idle && projectId) {
            if (props.object.id) {
                dispatch(fetchTags({projectId: parseInt(projectId)}))
            }
        }
    }, [status, dispatch])

    const elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);

    const showConfirmLayerDeletion = (item: Layer) => {
        confirm({
            title: 'Delete Layer',
            content: 'Are you sure you want to delete this layer? It will also delete all the animations associated with it.',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteLayer({projectId: parseInt(projectId), layerId: item.id}))
                }
            },
        });
    }

    const showConfirmAnimationDeletion = (item: Animation) => {
        confirm({
            title: 'Delete Animation',
            content: 'Are you sure you want to delete this animation?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteAnimation({
                        projectId: parseInt(projectId),
                        animationId: item.id
                    }))
                    setAnimation(undefined)
                }
            },
        });
    }

    return (
        <>
            <Flex gap={16}>
                <Flex align={'center'} style={{
                    minWidth: 400,
                    height: 400,
                    borderRadius: 6,
                    clipPath: 'border-box',
                    position: "relative",
                    background: '#eeeeee'
                }}>
                    <div
                        ref={elementRef}
                        style={{
                            position: 'relative',
                            width: '100%',
                            height: 'calc(100% / 1.77)'
                        }}>
                        {props.object.layers && props.object.layers.length > 0 ?
                            <div style={{
                                width: 1920,
                                height: 1080,
                                scale: `${scale}`,
                                overflow: 'hidden',
                                background: '#000',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                transformOrigin: 'top left'
                            }}>
                                <AnimatableElement
                                    element={{...props.object, x: 0, y: 0, z: 0, scale: 1}}
                                    play={play !== ShotState.Idle}
                                    scale={1}
                                    animations={props.object.animations && props.object.animations.length > 0 ? props.object.animations : []}
                                    onPlayEnd={(id: number) => {
                                        if (play) {
                                            setPlayed(prev => new Set(prev).add(id))
                                        }
                                    }}>

                                    {props.object.layers && props.object.layers.length > 0 ?
                                        props.object.layers.map(lay => {
                                            return <EditElement
                                                key={lay.id}
                                                prefix={"object-edit"}
                                                element={lay}
                                                play={play !== ShotState.Idle}
                                                onPlayEnd={() => {
                                                    if (play) {
                                                        setPlayed(prev => new Set(prev).add(lay.id ?? 0))
                                                    }
                                                }}
                                                selectedElement={layer}
                                                scale={scale}
                                                onElementSelected={(element) => {
                                                    setLayer(element)
                                                }}
                                                onMove={(info: { x: number, y: number, z: number, scale: number }) =>
                                                    setInfo(info)
                                                }
                                            />
                                        }) : ''
                                    }
                                </AnimatableElement></div> :
                            <div style={{
                                width: '100%',
                                height: '100%',
                                background: "#000000",
                                position: "relative"
                            }}>
                                <Starfield
                                    id={"object-edit-starfield"}
                                    starCount={5000}
                                    starColor={[255, 255, 255]}
                                    speedFactor={0.1}
                                    backgroundColor="black"
                                    scale={scale}/></div>
                        }
                    </div>
                    <Button icon={<FontAwesomeIcon icon={play !== ShotState.Idle ? faStop : faPlay}/>}
                            onClick={() => setPlay(play === ShotState.Playing ? ShotState.Idle : ShotState.Playing)}
                            style={{
                                lineHeight: '12px',
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 10000,
                                width: 38
                            }}
                            disabled={props.object.animations?.length == 0 && (!props.object.layers || props.object.layers.reduce((acc, curr) => acc + (curr.animations?.length || 0), 0) === 0)}
                    />
                    {layer ?
                        <>
                            <ElementProperties
                                style={{
                                    zIndex: 100000,
                                    position: 'absolute',
                                    right: 10,
                                    top: 52,
                                    background: '#fff',
                                    borderRadius: 6,
                                    padding: 2,
                                    bottom: 10
                                }}
                                element={layer}
                                onScaleChanged={(value) => {
                                    setLayer(
                                        {
                                            ...layer,
                                            scale: value
                                        } as Transformable
                                    )
                                    setInfo({...info, scale: value})
                                }}
                                onTransformed={(element) => {
                                    if (projectId && element && storyId) {
                                        dispatch(saveLayer({
                                            projectId: parseInt(projectId),
                                            storyId: parseInt(storyId),
                                            layer: element as Layer
                                        }))
                                    }
                                }}
                                onAlignmentChanged={() => {
                                }}
                            />
                            <ElementInfo element={info}/>
                        </> : ''}
                </Flex>
                <Space direction={'vertical'} style={{width: '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                       size={16}>
                    <div>
                        <Typography style={{fontWeight: 700}}>Title</Typography>
                        <Input value={props.title} onChange={(e) => props.onTitleChanged(e.target.value)}/>
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Tags</Typography>
                        <TagFlow allTags={allTags}
                                 tags={props.tags}
                                 onTagAdded={(tag) => {
                                     props.onTagsChanged([...props.tags, tag])
                                     if (props.object.id && projectId) {
                                         dispatch(addObjectTag({
                                             projectId: parseInt(projectId),
                                             objectId: props.object.id,
                                             tag: tag
                                         }))
                                     }
                                 }}
                                 onTagDeleted={(tag) => {
                                     if (tag.id && props.object.id && projectId) {
                                         dispatch(removeObjectTag({
                                             projectId: parseInt(projectId),
                                             objectId: props.object.id,
                                             tagId: tag.id
                                         }))
                                     }
                                 }}
                        />
                    </div>
                    <Flex align={'start'} gap={16}>
                        <Space direction={'vertical'}
                               style={{width: animation ? 300 : '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                        >
                            <div>
                                <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                                    <Typography style={{fontWeight: 700}}>Animations</Typography>
                                    <Button
                                        icon={<FontAwesomeIcon icon={faPlus}/>}
                                        type={'text'}
                                        onClick={() => {
                                            setAnimation({})
                                        }}
                                    />
                                </Flex>

                                <LayerList
                                    is={(item) => animation?.id === item.id}
                                    data={props.object.animations}
                                    title={(item) => item.title}
                                    clickable={true}
                                    onItemClick={(item) => {
                                        setAnimation(item)

                                    }}
                                    onDeleteClick={(item) => {
                                        showConfirmAnimationDeletion(item)
                                    }}
                                    onEditClick={(item) => {
                                        setAnimation(item)
                                    }}
                                />
                            </div>
                            <div>
                                <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                                    <Typography style={{fontWeight: 700}}>Layers</Typography>
                                    <Button
                                        icon={<FontAwesomeIcon icon={faPlus}/>}
                                        type={'text'}
                                        onClick={() =>
                                            setShowEditLayerModal({})
                                        }
                                    />
                                </Flex>

                                <LayerList
                                    is={(item) => layer?.id === item.id}
                                    data={props.object.layers?.map(lay => {
                                        return layer !== undefined && lay.id === layer.id ? {
                                            ...layer,
                                            layers: [layer],
                                            type: 'Layer'
                                        } as Combined : {...lay, layers: [lay], type: 'Layer'} as Combined
                                    })}
                                    clickable={true}
                                    itemStyle={{padding: 0}}
                                    title={(item, index) => <Flex align={'center'} gap={10}>
                                        <img src={(item as Layer).source}
                                             style={{
                                                 width: 50,
                                                 height: 50,
                                                 borderRadius: '5px',
                                                 objectFit: 'contain',
                                                 background: '#eeeeee'
                                             }}
                                        />
                                        {`Layer ${index + 1}`}
                                    </Flex>}
                                    onItemClick={(item) => {
                                        setLayer(item)
                                    }}
                                    onDeleteClick={(item) => {
                                        showConfirmLayerDeletion(item as Layer)
                                    }}
                                    onEditClick={(item) => {
                                        setLayer(item)
                                        setShowEditLayerModal(item as Layer)
                                    }}
                                />
                            </div>
                        </Space>
                        {animation ?
                            <AnimationForm
                                animation={animation}
                                pivotPoint={selectPivotPoint}
                                onSelectPivotPoint={(select: boolean) => setSelectPivotPoint(select)}
                                wrapper={(children) => {
                                    return <Space direction={'vertical'}
                                                  style={{width: 300, maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                                                  size={16}>{children} </Space>
                                }}
                                onSave={(animation: Animation) => {
                                    const origin = props.object.animations?.find(item => item.id === animation?.id)
                                    if (projectId && props.object.id && !compareAnimations(origin, animation)) {
                                        dispatch(
                                            saveObjectAnimation(
                                                {
                                                    projectId: parseInt(projectId),
                                                    objectId: props.object.id,
                                                    animation: animation
                                                }
                                            )
                                        )
                                    }
                                }}
                                onClose={() => setAnimation(undefined)}
                            />
                            : ''}
                    </Flex>
                </Space>
            </Flex>
            {showEditLayerModal ?
                <LayerEditModal
                    componentId={props.object?.component?.id}
                    key={showEditLayerModal?.id || '' + showEditLayerModal?.animations?.map(an => an.id)?.join('')}
                    layer={showEditLayerModal}
                    onCancel={() => setShowEditLayerModal(undefined)}
                    onSave={(layer) => {
                        if (projectId && layer && storyId && props.object.id) {
                            if (layer.id) {
                                dispatch(saveLayer({
                                    projectId: parseInt(projectId),
                                    storyId: parseInt(storyId),
                                    layer: layer
                                }))
                            } else {
                                dispatch(saveObjectLayer({
                                    projectId: parseInt(projectId),
                                    objectId: props.object.id,
                                    storyId: parseInt(storyId),
                                    layer: layer
                                }))
                            }
                        }
                        setShowEditLayerModal(undefined)
                    }}
                /> : ''}
        </>
    )
}